import type { FormulasState } from "@/hooks/useFormulas";
import type { Insurance } from "@/types/insurance/insurance";
import type { AgeInterval } from "@/types/parameter/parameter";
import { isValidAgeIntervals, toFixedNumber } from "../utils";

export function groupLifeHTA(
	insurance: Insurance | undefined,
	age: number,
	maritalStatus: FormulasState["maritalStatus"],
	childAges: number[],
) {
	let baseCoverage: number;
	let reductionInG: number;
	let startAge: number;
	let endAge: number;
	let ifNonProviderComp: number;
	let maxChildAge: number;
	let minAge: number;
	let maxAge: number;

	try {
		baseCoverage = parseFloat(
			insurance?.params?.find((param) => param.type === "base-compensation")
				?.value as string,
		);
		reductionInG = parseFloat(
			insurance?.params?.find((param) => param.type === "reduction-in-g")
				?.value as string,
		);
		startAge = parseFloat(
			insurance?.params?.find((param) => param.type === "start-age")
				?.value as string,
		);
		endAge = parseFloat(
			insurance?.params?.find((param) => param.type === "end-age")
				?.value as string,
		);
		ifNonProviderComp = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "if-non-provider-compensation",
			)?.value as string,
		);
		maxChildAge = parseFloat(
			insurance?.params?.find((param) => param.type === "max-age-child")
				?.value as string,
		);
		minAge = parseFloat(
			insurance?.params?.find((param) => param.type === "min-age")
				?.value as string,
		);
		maxAge = parseFloat(
			insurance?.params?.find((param) => param.type === "max-age")
				?.value as string,
		);
	} catch (error) {
		console.error("Error in additionalMedicalDisabilityCompensation:", error);
		return 0;
	}

	// Default max child age to 20 if not specified
	if (!maxChildAge || isNaN(maxChildAge)) {
		maxChildAge = 20;
	}

	// Check if person has any children under the max age
	const hasChildrenUnderMaxAge = childAges.some(
		(childAge) => childAge < maxChildAge,
	);

	// Person is a provider if they have a spouse/partner OR have children under max age
	const isProvider =
		!(maritalStatus === "single" || maritalStatus === null) ||
		hasChildrenUnderMaxAge;

	if (!isProvider && ifNonProviderComp < 0) {
		console.error("If non-provider compensation is out of range");
		return 0;
	}

	if (!isProvider && ifNonProviderComp > 0) {
		return ifNonProviderComp;
	}

	if (!baseCoverage || baseCoverage < 0) {
		return 0;
	}

	if (Number.isNaN(reductionInG) || reductionInG < 0) {
		console.error("Reduction in G is out of range");
		return 0;
	}

	if (!minAge) {
		minAge = 0;
	}

	if (!maxAge) {
		maxAge = 72;
	}

	if (!age || age < minAge || age >= maxAge) {
		console.error("Age is out of range", age);
		return 0;
	}

	if (!startAge || startAge < minAge || startAge >= maxAge) {
		console.error("Start age is out of range");
		return 0;
	}

	if (!endAge || endAge < minAge || endAge >= maxAge) {
		console.error("End age is out of range");
		return 0;
	}

	let total = baseCoverage;
	for (let i = startAge; i < age; i++) {
		if (i >= endAge) {
			break;
		}
		total -= reductionInG;
	}

	if (total < 0) {
		total = 0;
	}

	return toFixedNumber(total);
}



export function groupLifeIntervals(
	insurance: Insurance | undefined,
	age: number,
	annualSalary: number,
) {
	let intervals: AgeInterval[];
	let minPayout: number, maxPayout: number;
	try {
		intervals = insurance?.params?.find((param) => param.type === "intervals")
			?.value as AgeInterval[];
		minPayout = parseFloat(
			insurance?.params?.find((param) => param.type === "min-compensation")
				?.value as string,
		);
		maxPayout = parseFloat(
			insurance?.params?.find((param) => param.type === "max-compensation")
				?.value as string,
		);
	} catch (error) {
		console.error("Error in additionalMedicalDisabilityCompensation:", error);
		return 0;
	}

	if (isValidAgeIntervals(intervals) === false) {
		console.error("Invalid intervals");
		return 0;
	}

	if (!age) {
		console.error("Age is out of range");
		return 0;
	}

	if (!annualSalary || annualSalary < 0) {
		console.error("Annual salary is out of range");
		return 0;
	}

	if (minPayout < 0) {
		minPayout = 0;
	}

	if (maxPayout < 0) {
		console.error("Maximum payout is out of range");
		return 0;
	}

	let salaryMultiplier = 0;

	intervals.forEach((item) => {
		if (age >= Number(item.minAge) && age <= Number(item.maxAge)) {
			salaryMultiplier = Number(item.value);
		}
	});

	if (!salaryMultiplier) {
		return 0;
	}

	const payout = annualSalary * salaryMultiplier;

	if (payout < minPayout) {
		return minPayout;
	} else if (payout > maxPayout) {
		return maxPayout;
	}

	return toFixedNumber(payout);
}

/**
 * Calculates the payout for the group life annual salary insurance
 * @param insurance - The insurance object
 * @param annualSalary - The annual salary
 * @param age - The age
 * @returns The payout
 */
export function groupLifeAnnualSalary(
	insurance: Insurance | undefined,
	annualSalary: number,
	age: number,
) {
	let salaryMultiplier: number;
	let minPayout: number;
	let maxPayout: number;
	let minAge: number;
	let maxAge: number;

	try {
		salaryMultiplier = parseFloat(
			insurance?.params?.find((param) => param.type === "annual-salary")
				?.value as string,
		);
		minPayout = parseFloat(
			insurance?.params?.find((param) => param.type === "min-compensation")
				?.value as string,
		);
		maxPayout = parseFloat(
			insurance?.params?.find((param) => param.type === "max-compensation")
				?.value as string,
		);
		minAge = parseFloat(
			insurance?.params?.find((param) => param.type === "min-age")
				?.value as string,
		);
		maxAge = parseFloat(
			insurance?.params?.find((param) => param.type === "max-age")
				?.value as string,
		);
	} catch (error) {
		console.error("Error in additionalMedicalDisabilityCompensation:", error);
		return 0;
	}

	if (!salaryMultiplier || salaryMultiplier < 0) {
		console.error("Salary multiplier is out of range");
		return 0;
	}

	if (!annualSalary || annualSalary < 0) {
		console.error("Annual salary is out of range");
		return 0;
	}

	if (!minAge) {
		minAge = 0;
	}

	if (!maxAge) {
		maxAge = 72;
	}

	if (!age || age < minAge || age >= maxAge) {
		console.error("Age is out of range");
		return 0;
	}

	if (minPayout < 0) {
		minPayout = 0;
	}

	if (maxPayout < 0) {
		console.error("Maximum payout is out of range");
		return 0;
	}

	const payout = annualSalary * salaryMultiplier;

	if (payout < minPayout) {
		return minPayout;
	} else if (payout > maxPayout) {
		return maxPayout;
	}

	return toFixedNumber(payout);
}

export function groupLifeBaseCoverage(
	insurance: Insurance | undefined,
	age: number,
) {
	let baseCoverageParam: number;
	let minAge: number;
	let maxAge: number;

	try {
		baseCoverageParam = parseFloat(
			insurance?.params?.find((param) => param.type === "base-compensation")
				?.value as string,
		);
		minAge = parseFloat(
			insurance?.params?.find((param) => param.type === "min-age")
				?.value as string,
		);
		maxAge = parseFloat(
			insurance?.params?.find((param) => param.type === "max-age")
				?.value as string,
		);
	} catch (error) {
		console.error("Error in additionalMedicalDisabilityCompensation:", error);
		return 0;
	}

	if (!minAge) {
		minAge = 0;
	}

	if (!maxAge) {
		maxAge = 72;
	}

	if (!age || age < minAge || age >= maxAge) {
		console.error("Age is out of range");
		return 0;
	}

	if (!baseCoverageParam || baseCoverageParam < 0) {
		return 0;
	}

	return toFixedNumber(baseCoverageParam);
}
export function groupLifeChildSupplement(
	insurance: Insurance | undefined,
	childAges: number[],
	soleProvider: boolean,
) {
	let baseCoverageParam: number;
	let minAge: number;
	let maxAge: number;
	let sum = 0;

	try {
		baseCoverageParam = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "base-compensation-child-supplement",
			)?.value as string,
		);
		minAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "min-age-child-supplement",
			)?.value as string,
		);
		maxAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "max-age-child-supplement",
			)?.value as string,
		);
	} catch (error) {
		console.error("Error in groupLifeChildSupplement:", error);
		return 0;
	}

	if (!minAge) {
		minAge = 0;
	}

	if (!maxAge) {
		maxAge = 20;
	}

	// Age-based reduction table (same as leisureAccidentDeath)
	const childAddon: { [key: number]: number } = {
		0: 6.5,
		1: 6.0,
		2: 6.0,
		3: 5.5,
		4: 5.0,
		5: 5.0,
		6: 4.5,
		7: 4.0,
		8: 4.0,
		9: 3.5,
		10: 3.5,
		11: 3.5,
		12: 2.5,
		13: 2.5,
		14: 2.0,
		15: 2.0,
		16: 1.5,
		17: 1.5,
		18: 1.0,
		19: 1.0,
	};

	for (const age of childAges) {
		if (age < minAge || age >= maxAge) {
			continue;
		}

		let childSupplement = 0;

		// If base compensation is set and valid, use it; otherwise use age-based reduction table
		if (
			baseCoverageParam &&
			!Number.isNaN(baseCoverageParam) &&
			baseCoverageParam > 0
		) {
			childSupplement = baseCoverageParam;
		} else if (age >= 0 && age < 20) {
			childSupplement = childAddon[age];
		} else if (age >= 20 && age <= maxAge) {
			childSupplement = 1;
		}

		if (soleProvider) {
			childSupplement = childSupplement * 2;
		}

		sum += childSupplement;
	}

	return toFixedNumber(sum);
}

export function groupLifeGradualReduction(
	insurance: Insurance | undefined,
	age: number,
	salary: number,
) {
	let annualSalaryMultiplier: number;
	let reduction: number;
	let startAge: number;
	let forNumberOfYears: number;
	let minAge: number;
	let maxAge: number;

	try {
		annualSalaryMultiplier = parseFloat(
			insurance?.params?.find((param) => param.type === "annual-salary")
				?.value as string,
		);
		reduction =
			parseFloat(
				insurance?.params?.find((param) => param.type === "reduction")
					?.value as string,
			) / 100;
		startAge = parseFloat(
			insurance?.params?.find((param) => param.type === "start-age")
				?.value as string,
		);
		forNumberOfYears = parseFloat(
			insurance?.params?.find((param) => param.type === "for-number-of-years")
				?.value as string,
		);
		minAge = parseFloat(
			insurance?.params?.find((param) => param.type === "min-age")
				?.value as string,
		);
		maxAge = parseFloat(
			insurance?.params?.find((param) => param.type === "max-age")
				?.value as string,
		);
	} catch (error) {
		console.error("Error in additionalMedicalDisabilityCompensation:", error);
		return 0;
	}

	if (!annualSalaryMultiplier || annualSalaryMultiplier < 0) {
		console.error("Annual salary multiplier is out of range");
		return 0;
	}

	if (!minAge) {
		minAge = 0;
	}

	if (!maxAge) {
		maxAge = 72;
	}

	if (!age || age < minAge || age > maxAge) {
		console.error("Age is out of range");
		return 0;
	}

	if (!salary || salary < 0) {
		console.error("Salary is out of range");
		return 0;
	}

	if (!startAge || startAge < 20 || startAge > 70) {
		console.error("Start age is out of range");
		return 0;
	}

	if (forNumberOfYears < 0) {
		console.error("For number of years is out of range");
		return 0;
	}

	if (reduction < 0 || reduction > 1) {
		console.error("Reduction is out of range");
		return 0;
	}

	let sum = salary * annualSalaryMultiplier;

	if (age > startAge && age <= startAge + forNumberOfYears) {
		sum -= reduction * (age - startAge) * salary * annualSalaryMultiplier;
	}

	if (age > startAge + forNumberOfYears) {
		sum -= reduction * forNumberOfYears * salary * annualSalaryMultiplier;
	}

	if (sum < 0) {
		return 0;
	}

	return toFixedNumber(sum);
}

export function groupLifeAnnualSalaryAboveXG(
	insurance: Insurance | undefined,
	salary: number,
	age: number,
) {
	let annualSalaryMultiplier: number;
	let annualSalaryAboveXG: number;
	let maxPayout: number;
	let minAge: number;
	let maxAge: number;

	try {
		annualSalaryMultiplier = parseFloat(
			insurance?.params?.find((param) => param.type === "annual-salary")
				?.value as string,
		);
		annualSalaryAboveXG = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "annual-salary-above-x-g",
			)?.value as string,
		);
		maxPayout = parseFloat(
			insurance?.params?.find((param) => param.type === "max-compensation")
				?.value as string,
		);
		minAge = parseFloat(
			insurance?.params?.find((param) => param.type === "min-age")
				?.value as string,
		);
		maxAge = parseFloat(
			insurance?.params?.find((param) => param.type === "max-age")
				?.value as string,
		);
	} catch (error) {
		console.error("Error in additionalMedicalDisabilityCompensation:", error);
		return 0;
	}

	if (!annualSalaryMultiplier || annualSalaryMultiplier < 0) {
		console.error("Annual salary multiplier is out of range");
		return 0;
	}

	if (!annualSalaryAboveXG || annualSalaryAboveXG < 0) {
		console.error("Annual salary above X G is out of range");
		return 0;
	}

	if (!salary || salary < 0) {
		console.error("Salary is out of range");
		return 0;
	}

	if (!minAge) {
		minAge = 0;
	}

	if (!maxAge) {
		maxAge = 72;
	}

	if (!age || age < minAge || age > maxAge) {
		console.error("Age is out of range");
		return 0;
	}

	if (maxPayout < 0) {
		console.error("Maximum payout is out of range");
		return 0;
	}

	let amount = salary - annualSalaryAboveXG;

	if (amount <= 0) {
		return 0;
	}

	amount *= annualSalaryMultiplier;

	if (amount > maxPayout) {
		return toFixedNumber(maxPayout);
	}

	return toFixedNumber(amount);
}
